import { router } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { useMarket } from "@/lib/useMarket"
import { revenue_path, revenue_products_path } from "@/lib/routes"
import RevenueToolbar from "./components/RevenueToolbar"
import RevenueChart from "./components/RevenueChart"
import RevenuePivotTable from "./components/RevenuePivotTable"
import ProductPicker from "./components/ProductPicker"
import ScopeBreadcrumb from "./components/ScopeBreadcrumb"
import { formatRange } from "./format"
import { hrefFor, toQuery } from "./query"
import { nextSort } from "./sort"
import { GRAIN_LABEL, type Filters, type PivotRow, type RevenuePageProps } from "./types"

export default function RevenueIndex({ filters, pivot, scope, scope_tree, currency }: RevenuePageProps) {
  const market = useMarket()
  const scopeSlug = scope.id === market.id ? null : scope.slug
  const scopeLabel = scope.id === market.id ? null : scope.name

  const update = (patch: Partial<Filters>) => {
    router.get(revenue_path(market.slug), toQuery({ ...filters, ...patch }, scopeSlug), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const products = filters.rows === "products"
  const hrefForScope = (slug: string) => hrefFor(market.slug, filters, slug === market.slug ? null : slug)
  // Channel rows drill down; the row that is the scope itself (childless scope,
  // or its "(direct)" row) would only reload the same view, so it stays plain.
  const hrefForRow = (row: PivotRow) => (row.slug && row.key !== scope.id ? hrefForScope(row.slug) : null)
  const onSort = (key: string) => update(nextSort({ sort: filters.sort, dir: filters.dir }, key))
  const chartTitle = `Revenue by ${GRAIN_LABEL[filters.grain]}`
  const chartSubtitle = [market.name, scopeLabel, formatRange(filters.from, filters.to), products ? "Listed products only" : null]
    .filter(Boolean)
    .join(" · ")

  const productsHeader = products ? (
    <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
      <div>
        <div className="text-sm font-semibold">
          {pivot.auto_products ? "Top products by revenue" : `Picked products (${pivot.rows.length})`}
        </div>
        <div className="text-xs text-muted-foreground">
          {pivot.auto_products
            ? "Showing the best sellers for this period. Add products to compare a fixed list instead."
            : "Only the products you picked. Remove one with the × next to its name."}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!pivot.auto_products && (
          <Button variant="ghost" size="sm" onClick={() => update({ products: [] })}>
            Back to top sellers
          </Button>
        )}
        <ProductPicker
          searchUrl={revenue_products_path(market.slug)}
          picked={filters.products}
          onAdd={(id) => update({ products: [...(pivot.auto_products ? [] : filters.products), id] })}
        />
      </div>
    </div>
  ) : null

  return (
    <AppLayout header={<PageTitle title="Revenue" subtitle="Sales revenue across channels and products" />}>
      <div className="flex flex-col gap-4">
        <RevenueToolbar
          filters={filters}
          scope={scope}
          scopeTree={scope_tree}
          onChange={update}
          hrefForScope={hrefForScope}
        />
        <RevenueChart
          pivot={pivot}
          rows={filters.rows}
          currency={currency}
          title={chartTitle}
          subtitle={chartSubtitle}
          totalLabel={products ? "Total revenue, listed products" : "Total revenue"}
        />
        <RevenuePivotTable
          pivot={pivot}
          rows={filters.rows}
          currency={currency}
          rowsHeading={products ? "Product" : "Channel"}
          sort={filters.sort}
          dir={filters.dir}
          onSort={onSort}
          header={
            <>
              <ScopeBreadcrumb scopeTree={scope_tree} scopeId={scope.id} marketId={market.id} hrefForScope={hrefForScope} />
              {productsHeader}
            </>
          }
          hrefForRow={products ? undefined : hrefForRow}
          onRemoveRow={
            products && !pivot.auto_products
              ? (row) => update({ products: filters.products.filter((id) => id !== row.key) })
              : undefined
          }
        />
      </div>
    </AppLayout>
  )
}
